import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Stack,
  Group,
  Title,
  Text,
  Button,
  Card,
  SimpleGrid,
  Box,
  ThemeIcon,
} from '@mantine/core'
import { IconCheck } from '@tabler/icons-react'
import { useModalStack } from '../ui/ModalStack'

export default function PricingPage() {
  const navigate = useNavigate()
  const { pushModal } = useModalStack()
  const noteIcon = `${process.env.PUBLIC_URL}/tunistry-note-icon-white.svg`

  const handleFreePlan = () => {
    pushModal('emailCreateAccount')
  }

  const handleDJPassPlan = () => {
    pushModal('emailCreateAccount')
  }

  const FeatureRow = ({ children }) => (
    <Group gap="sm" wrap="nowrap" mb={8}>
      <ThemeIcon color="blue" size={20} radius="xl" variant="light" style={{ flexShrink: 0 }}>
        <IconCheck size={12} />
      </ThemeIcon>
      <Text size="sm">{children}</Text>
    </Group>
  )

  return (
    <Box>
      {/* Header */}
      <Box component="header" style={{
        position: 'relative',
        zIndex: 1,
        background: 'rgba(0,0,0,0.25)',
        backdropFilter: 'blur(4px)',
        borderBottom: '1px solid rgba(255,255,255,0.08)'
      }}>
        <Container size="lg" py="sm">
          <Group justify="space-between" align="center">
            <Group align="center" gap="sm">
              <img src={noteIcon} alt="Tunistry note icon" width={28} height={28} style={{ display: 'block' }} />
              <Title
                order={2}
                style={{
                  margin: 0,
                  fontSize: 32,
                  fontWeight: 700,
                  fontFamily: 'Cairo, Arial, sans-serif',
                  letterSpacing: 0.2,
                }}
              >
                Tunistry
              </Title>
            </Group>
            <Group>
              <Button size="sm" radius="sm" variant="subtle" onClick={() => navigate('/')}>
                ← Back
              </Button>
            </Group>
          </Group>
        </Container>
      </Box>

      {/* Hero */}
      <Box
        style={{
          background:
            'radial-gradient(1200px 400px at 20% -10%, rgba(99,102,241,0.35), transparent 60%), radial-gradient(1000px 300px at 80% -10%, rgba(236,72,153,0.25), transparent 60%)',
        }}
      >
        <Container size="lg" py="xl" style={{ paddingBottom: 0 }}>
          <Stack gap="md" mt="lg" mb="lg" align="center">
            <Title order={1} style={{ fontSize: 36, lineHeight: 1.1, textAlign: 'center' }}>
              Simple pricing. Serious music.
            </Title>
            <Text size="lg" style={{ textAlign: 'center', maxWidth: 600 }}>
              Start free with full music and curation access, plus a limited amount of complimentary DJ access.
              Upgrade to a paid plan to unlock more DJ conversations after your free introduction.
            </Text>
          </Stack>
        </Container>
      </Box>

      {/* Pricing Cards */}
      <Container size="md" py="xl">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">

          {/* Free Plan */}
          <Card
            withBorder
            radius="lg"
            p="xl"
            style={{
              background: 'rgba(255,255,255,0.03)',
              borderColor: 'rgba(255,255,255,0.12)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack gap="md" style={{ flex: 1 }}>
              <Box style={{ textAlign: 'center' }}>
                <Title order={1} mb={4}>Free</Title>
                <Group gap="xs" align="baseline" mb="sm" justify="center">
                  <Text size="xl" fw={700}>$0</Text>
                </Group>
                <Text size="md" align="left">
                  Everything you need to listen, discover, and collaborate with a DJ trial period.
                </Text>
              </Box>

              <Box>
                <Text align="left" size="md" fw={600} tt="uppercase" mb="sm">What's included:</Text>
                <FeatureRow>Full access to all music and rooms</FeatureRow>
                <FeatureRow>React, chat, and listen with others</FeatureRow>
                <FeatureRow>Manage tags and playlists</FeatureRow>
                <FeatureRow>Collaborate on room curation</FeatureRow>
                <FeatureRow>Introductory DJ access — try it out</FeatureRow>
                <FeatureRow>No credit card required</FeatureRow>
              </Box>

              <Box mt="auto" pt="md">
                <Button fullWidth size="md" radius="sm" variant="filled" color="indigo" onClick={handleFreePlan}>
                  Get started free
                </Button>
              </Box>
            </Stack>
          </Card>

          {/* Resident DJ */}
          <Card
            withBorder
            radius="lg"
            p="xl"
            style={{
              background: 'linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(236,72,153,0.08) 100%)',
              borderColor: 'rgba(99,102,241,0.4)',
              boxShadow: '0 8px 32px rgba(99,102,241,0.15)',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}
          >
            <Stack gap="md" style={{ flex: 1 }}>
              <Box style={{ textAlign: 'center' }}>
                <Title order={1} mb={4}>Resident DJ</Title>
                <Group gap="xs" align="baseline" mb="sm" justify="center">
                  <Text size="xl" fw={700} c="indigo">$7</Text>
                  <Text size="md">/ month</Text>
                </Group>
                <Text size="md" align="left">
                  The full Tunistry experience. Your personal music expert and DJ.
                </Text>
              </Box>

              <Box>
                <Text align="left" size="md" fw={600} tt="uppercase" mb="sm">Everything in the Free plan, plus:</Text>
                <FeatureRow>Personalized sets built for you and your friends</FeatureRow>
                <FeatureRow>Deep music insights — artist history, context, and more</FeatureRow>
                <FeatureRow>Smarter recommendations as your DJ gets to know you</FeatureRow>
                <FeatureRow>Cancel anytime</FeatureRow>
              </Box>

              <Box mt="auto" pt="md">
                <Button fullWidth size="md" radius="sm" variant="filled" color="indigo" onClick={handleDJPassPlan}>
                  Get Resident DJ
                </Button>
              </Box>
            </Stack>
          </Card>
        </SimpleGrid>

        {/* Fine print */}
        <Text size="xs" style={{ textAlign: 'center', marginTop: 16, maxWidth: 600, margin: '16px auto 0' }}>
          * Resident DJ includes DJ conversations subject to a monthly fair-use limit.
        </Text>
      </Container>

      {/* FAQ */}
      <Container size="lg" py="xl">
        <Card
          withBorder
          radius="lg"
          p="xl"
          style={{
            background: 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(236,72,153,0.06) 100%)',
            borderColor: 'rgba(255,255,255,0.18)'
          }}
        >
          <Title order={3} style={{ textAlign: 'center', marginBottom: 24 }}>
            Frequently Asked Questions
          </Title>
          <Stack gap="lg" style={{ textAlign: 'left' }}>
            <Box>
              <Text fw={600} mb="xs">What does "introductory DJ access" on the free plan mean?</Text>
              <Text size="sm">
                Free users can have a limited number of DJ conversations — plenty to get a feel for
                how it works and shapes your listening experience. If you find yourself wanting more,
                the Resident DJ plan gives you full access.
              </Text>
            </Box>
            <Box>
              <Text fw={600} mb="xs">What can I do with the DJ?</Text>
              <Text size="sm">
                You can ask your DJ to play a mood, a genre, a specific era, or your personal favorites.
                You can also ask about artists and tracks, request discoveries based on what you love,
                and guide the set in real time. The DJ learns your taste over time and adapts accordingly.
              </Text>
            </Box>
            <Box>
              <Text fw={600} mb="xs">What's included in "curation"?</Text>
              <Text size="sm">
                Both free and paid users can tag tracks, react to music, and collaborate
                with others in a room to shape what plays. Curation tools are fully available to room owners
                and their trusted curators on both plans — the DJ is the only thing that differs.
              </Text>
            </Box>
            <Box>
              <Text fw={600} mb="xs">Can I upgrade or downgrade at any time?</Text>
              <Text size="sm">
                Yes — you can upgrade, downgrade, or cancel anytime. Your tags are always preserved regardless
                of your plan.
              </Text>
            </Box>
            <Box>
              <Text fw={600} mb="xs">Why does the DJ cost money?</Text>
              <Text size="sm">
                AI-powered DJ conversations have real costs behind them. The Resident DJ plan is priced
                to cover those costs while keeping the experience sustainable for everyone.
              </Text>
            </Box>
          </Stack>
        </Card>
      </Container>

      {/* CTA */}
      <Container size="lg" py="xl">
        <Box style={{ textAlign: 'center' }}>
          <Title order={2} mb="md">Start the conversation.</Title>
          <Text size="lg" mb="lg" style={{ maxWidth: 560, margin: '0 auto 24px' }}>
            Join a public room as a guest to see what the hype is all about.
            Sign up when you're ready to create a room of your own.
          </Text>
          <Group justify="center" gap="md">
            <Button size="lg" radius="sm" variant="light" onClick={() => navigate('/', { state: { scrollToRooms: true } })}>
              Browse rooms
            </Button>
            <Button
              size="lg"
              radius="sm"
              variant="filled"
              color="indigo"
              onClick={handleFreePlan}
            >
              Sign up
            </Button>
          </Group>
        </Box>
      </Container>
    </Box>
  )
}
